// Option lists shared with the project modal: one source, so a value that can
// be picked in the modal can always be filtered on here.
import {
  PROJECT_STANDARD_OPTIONS,
  PROJECT_STRUCTURE_TYPE_OPTIONS,
  PROJECT_WALL_TYPE_OPTIONS,
  PROJECT_ROOF_TYPE_OPTIONS,
  PROJECT_FLOOR_TYPE_OPTIONS,
} from '../../components/ProjectDetailModal/config';

export {
  PROJECT_STANDARD_OPTIONS,
  PROJECT_STRUCTURE_TYPE_OPTIONS,
  PROJECT_WALL_TYPE_OPTIONS,
  PROJECT_ROOF_TYPE_OPTIONS,
  PROJECT_FLOOR_TYPE_OPTIONS,
};

// =======================================
// DATA TYPES
// =======================================

/**
 * Project data structure as received from the database
 */
export interface Project {
  id: number;
  job_number: number | null;
  description: string;
  status: string;  // Using string since it's an enum from Supabase
  types: string;   // Using string since it's an enum from Supabase
  standard: string | null; // Using string since it's an enum from Supabase, but might be null
  created_at: string;
  project_lead: {
    id: number | string;
    first_name: string;
    last_name: string;
  } | null;

  client: {
    id: number | string;
    company_name: string;
  } | null;
  wall_type?: string[] | null;
  roof_type?: string[] | null;
  floor_type?: string[] | null;
  structure_type?: string[] | null;
  wind_region?: string | null;
  site_classification?: string | null;
  key_words?: string | null;
}

/**
 * Detailed project data structure for the modal view
 */
export interface ProjectDetail {
  id: number;
  job_number: number | null;
  description: string;
  status: string;
  types: string;
  standard: string | null;
  created_at: string;
  updated_at?: string;
  wall_type: string[] | null;
  roof_type: string[] | null;
  floor_type: string[] | null;
  structure_type: string[] | null;
  wind_region: string | null;
  site_classification: string | null;
  key_words: string | null;
  project_lead: {
    id: number | string;
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
  client: {
    id: number | string;
    company_name: string;
  } | null;
}

/**
 * Project stage data structure as received from the database
 */
export interface ProjectStage {
  id: number;
  parent_project_id: number;
  stage_id: string;
  stage_description: string;
  job_number: string; // Changed to string to support "15955.1" format
  status: string;
  project_lead_info: {
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
  created_at: string;
  updated_at: string;
}

/**
 * Form data structure for creating/editing stages
 */
export interface StageFormData {
  stage_id: string;
  stage_description: string;
  project_lead?: string;
  status: string;
}

/**
 * Unified project/stage option for timesheet selection
 */
export interface TimesheetProjectOption {
  id: number;
  job_number: number;
  display_name: string;
  status: string;
  entity_type: 'project' | 'stage';
  parent_project_id?: number;
  stage_id?: string;
  stage_description?: string;
  effective_project_lead: string;
}

/**
 * Pagination information returned from server-side filtering
 */
export interface PaginationInfo {
  page: number;
  page_size: number;
  total_pages: number;
  total_records: number;
  has_next: boolean;
  has_previous: boolean;
}

/**
 * Response structure from server-side filtering RPC function
 */
export interface ProjectListResponse {
  projects: Project[];
  total_count: number;
  page_info: PaginationInfo;
}

/**
 * Filter configuration for server-side filtering
 */
export interface ProjectFilters {
  search_term?: string | null;
  status_filter?: string[] | null;
  types_filter?: string[] | null;
  client_filter?: string[] | null;
  project_lead_filter?: string[] | null;
  wall_type_filter?: string[] | null;
  roof_type_filter?: string[] | null;
  floor_type_filter?: string[] | null;
  structure_type_filter?: string[] | null;
  wind_region_filter?: string[] | null;
  site_classification_filter?: string[] | null;
  page_size?: number;
  page_offset?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

// =======================================
// COMPONENT CONFIGURATION
// =======================================

/**
 * Configuration for table columns (display-only)
 */
export interface ColumnConfig {
  type: 'text' | 'select' | 'badge';
  label: string;
  order: number;
}

/**
 * Configuration for modal form fields
 */
export interface ModalFieldConfig {
  type: 'text' | 'select' | 'readonly';
  label: string;
  options?: readonly string[];
  required?: boolean;
  section: 'basic' | 'people' | 'technical' | 'other';
  order: number;
}

/**
 * Configuration for pagination component
 */
export interface PaginationConfig {
  showPageInfo: boolean;
  showQuickJump: boolean;
  maxVisiblePages: number;
  pageSizeOptions: readonly number[];
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

/**
 * Available project job type options
 */
export const PROJECT_JOB_TYPE_OPTIONS = [
  'Design',
  'Advisory',
  'RWC',
  'RWD'
] as const;

export type ProjectJobType = typeof PROJECT_JOB_TYPE_OPTIONS[number];

export type ProjectStandard = typeof PROJECT_STANDARD_OPTIONS[number];

export type ProjectWallType = typeof PROJECT_WALL_TYPE_OPTIONS[number];

export type ProjectRoofType = typeof PROJECT_ROOF_TYPE_OPTIONS[number];

/**
 * Wind region options for projects
 */
export const PROJECT_WIND_REGION_OPTIONS = [
  'A0',
  'A1',
  'A2',
  'A3',
  'A4',
  'A5',
  'B1',
  'B2',
  'C',
  'D'
] as const;

export type ProjectWindRegion = typeof PROJECT_WIND_REGION_OPTIONS[number];

/**
 * Site classification options for projects
 */
export const PROJECT_SITE_CLASSIFICATION_OPTIONS = [
  'A',
  'S',
  'M',
  'M-D',
  'H1',
  'H1-D',
  'H2',
  'H2-D',
  'E'
] as const;

export type ProjectSiteClassification = typeof PROJECT_SITE_CLASSIFICATION_OPTIONS[number];

/**
 * Default pagination settings
 */
export const DEFAULT_PAGINATION_CONFIG: PaginationConfig = {
  showPageInfo: true,
  showQuickJump: false,
  maxVisiblePages: 5,
  pageSizeOptions: [25, 50, 100]
} as const;

/**
 * Default page size for project listing
 */
export const DEFAULT_PAGE_SIZE = 50;

/**
 * Debounce delay for search input in milliseconds
 */
export const SEARCH_DEBOUNCE_DELAY = 300;

/**
 * Default stage form data
 */
export const DEFAULT_STAGE_FORM_DATA: StageFormData = {
  stage_id: '',
  stage_description: '',
  project_lead: '',
  status: 'Probable'
} as const;

// =======================================
// COMPONENT SETTINGS
// =======================================

/**
 * Column configuration for the project table (display-only)
 */
export const projectColumns: Record<string, ColumnConfig> = {
  job_number: {
    type: 'text',
    label: 'Job #',
    order: 1
  },
  created_at: {
    type: 'text',
    label: 'Created',
    order: 2
  },
  description: {
    type: 'text',
    label: 'Description',
    order: 3
  },
  client: {
    type: 'text',
    label: 'Client',
    order: 4
  },
  types: {
    type: 'badge',
    label: 'Type',
    order: 5
  },
  status: {
    type: 'badge',
    label: 'Status',
    order: 6
  },
  project_lead: {
    type: 'text',
    label: 'Lead', // an initials bubble; the full name is on hover
    order: 7
  }
};

/**
 * Modal form field configuration for project details
 */
export const projectModalFields: Record<string, ModalFieldConfig> = {
  // Basic Information Section
  job_number: {
    type: 'readonly',
    label: 'Job Number',
    section: 'basic',
    order: 1
  },
  description: {
    type: 'text',
    label: 'Description',
    required: true,
    section: 'basic',
    order: 2
  },
  status: {
    type: 'select',
    label: 'Status',
    // Options are fetched dynamically from workflow_statuses based on project type
    required: true,
    section: 'basic',
    order: 3
  },
  types: {
    type: 'select',
    label: 'Type',
    options: PROJECT_JOB_TYPE_OPTIONS,
    required: true,
    section: 'basic',
    order: 4
  },
  standard: {
    type: 'select',
    label: 'Standard',
    options: PROJECT_STANDARD_OPTIONS,
    section: 'basic',
    order: 5
  },
  
  // People Section
  project_lead: {
    type: 'select',
    label: 'Project Lead',
    section: 'people',
    order: 1
  },
  client: {
    type: 'select',
    label: 'Client',
    section: 'people',
    order: 2
  },
  
  // Technical Section
  wall_type: {
    type: 'select',
    label: 'Wall Type',
    options: PROJECT_WALL_TYPE_OPTIONS,
    section: 'technical',
    order: 1
  },
  roof_type: {
    type: 'select',
    label: 'Roof Type',
    options: PROJECT_ROOF_TYPE_OPTIONS,
    section: 'technical',
    order: 2
  },
  wind_region: {
    type: 'select',
    label: 'Wind Region',
    options: PROJECT_WIND_REGION_OPTIONS,
    section: 'technical',
    order: 3
  },
  site_classification: {
    type: 'select',
    label: 'Site Classification',
    options: PROJECT_SITE_CLASSIFICATION_OPTIONS,
    section: 'technical',
    order: 4
  },
  
  // Other Section
  // key_words has been replaced by tags (see TagSelector component)
  created_at: {
    type: 'readonly',
    label: 'Created',
    section: 'other',
    order: 2
  },
  updated_at: {
    type: 'readonly',
    label: 'Updated',
    section: 'other',
    order: 3
  }
}; 